import { useState } from 'react';
import Modal from './Modal';
import { useAppSelector, useAppDispatch } from '../hooks/redux';
import { executeSophiaAction } from '../features/thread/threadSlice';
import { ProjectType } from '../api/sophia';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectType?: ProjectType;
}

export function FeedbackModal({ isOpen, onClose, projectType }: FeedbackModalProps) {
  const [feedbackText, setFeedbackText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const dispatch = useAppDispatch();
  const reduxState = useAppSelector((state) => state);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!feedbackText.trim()) {
      setSubmitError('Il feedback non può essere vuoto');
      return;
    }

    if (!projectType) {
      setSubmitError('Tipo di progetto non disponibile');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    try {
      // Determine the action name based on project type
      const actionName = projectType === 'optimization' 
        ? 'record-feedback-optimization' 
        : 'record-feedback-consultation';

      // Execute the action with Redux state as JSON object
      await dispatch(executeSophiaAction({
        actionName,
        params: {
          'feedback-text': feedbackText,
          'additional-feedback-metadata': reduxState,
        },
        refreshThread: false, // No need to refresh thread after feedback
      })).unwrap();

      setSubmitSuccess(true);
      setFeedbackText('');
      
      // Close modal after a brief delay to show success message
      setTimeout(() => {
        setSubmitSuccess(false);
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setSubmitError(error instanceof Error ? error.message : 'Errore durante l\'invio del feedback');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFeedbackText('');
      setSubmitError(null);
      setSubmitSuccess(false);
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div className="flex max-w-[95vw] flex-col bg-white" style={{ width: '600px' }}>
        <div className="flex w-full items-center justify-between gap-2 bg-white p-2 border-b border-gray-200">
          <div className="flex flex-row items-center justify-start">
            <button type="button" onClick={handleClose} disabled={isSubmitting} className="border-r-2 p-2 pr-4 pb-3 text-2xl disabled:opacity-50">×</button>
            <h2 className="pl-3 text-2xl font-bold">Invia Feedback</h2>
          </div>
        </div>

        {submitSuccess && (
          <div className="mx-4 mt-4 bg-green-50 p-4 text-green-800 border border-green-200">
            <div className="flex items-center">
              <i className="ti-check mr-2 text-xl" aria-hidden="true" />
              <span>Feedback inviato con successo!</span>
            </div>
          </div>
        )}

        {submitError && (
          <div className="mx-4 mt-4 bg-red-50 p-4 text-red-800 border border-red-200">
            <div className="flex items-center">
              <i className="ti-alert mr-2 text-xl" aria-hidden="true" />
              <span>{submitError}</span>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col flex-1">
          <div className="flex-1 p-4">
            <label htmlFor="feedback-text" className="mb-2 block text-sm font-medium text-gray-700">
              Il tuo feedback
            </label>
            <textarea
              id="feedback-text"
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              disabled={isSubmitting}
              rows={8}
              className="w-full border border-gray-300 p-3 focus:border-gray-500 focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
              placeholder="Descrivi il tuo feedback..."
              required
            />
            <p className="mt-2 text-sm text-gray-500">
              Il feedback verrà inviato insieme alle informazioni sullo stato corrente del progetto.
            </p>
          </div>

          <div className="flex justify-end items-center w-full p-4 bg-white border-t border-gray-200">
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-semibold bg-white text-black border-2 border-black disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Annulla
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !feedbackText.trim()}
                className="px-4 py-2 text-sm font-semibold bg-black text-white border-2 border-black disabled:bg-gray-300 disabled:border-gray-300 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Invio...' : 'Invia Feedback'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
}

export default FeedbackModal;

